import { ClearOutlined, ExpandMore } from '@mui/icons-material';
import { IconButton, InputAdornment, MenuItem, TextField } from '@mui/material';
import type { ReactNode } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import {
  hasFieldHelpIcon,
  normalizeFieldHelpProps,
  useVNFieldHelpContext,
  VNFieldHelpIconButton,
  VNFieldHelpLayout,
  VNFieldHelpMenuItem,
  VNFieldTopLabel,
} from '@/components/VNFieldHelp';

type Props = VNFieldHelpProps & {
  name: string;
  label: string;
  disabled?: boolean;
  options: { label: ReactNode; value: string | number }[];
  renderValue?: (value: string | number) => ReactNode;
  tooltip?: string;
  rules?: RegisterOptions;
  required?: boolean;
  defaultValue?: any;
  clearOption?: boolean;
  onBeforeChange?: (newValue: string, oldValue: string) => boolean | Promise<boolean>;
  /** false (default) = floating label; true = static label above the control. */
  ver2?: boolean;
};

type SelectFieldProps = {
  name: string;
  label: string;
  disabled: boolean;
  required: boolean;
  tooltip: string;
  showFieldHelp: boolean;
  clearOption: boolean;
  options: { label: ReactNode; value: string | number }[];
  renderValue?: (value: string | number) => ReactNode;
  hasItemHelp: boolean;
  dropDownItemHelperTooltip?: Record<string, string>;
  dropDownItemHelperText?: Record<string, string>;
  onBeforeChange?: (newValue: string, oldValue: string) => boolean | Promise<boolean>;
  defaultValue?: any;
  rules?: RegisterOptions;
  ver2: boolean;
};

const VNSelectField = ({
  name,
  label,
  disabled,
  required,
  tooltip,
  showFieldHelp,
  clearOption,
  options,
  renderValue,
  hasItemHelp,
  dropDownItemHelperTooltip,
  dropDownItemHelperText,
  onBeforeChange,
  defaultValue,
  rules = {},
  ver2,
}: SelectFieldProps) => {
  const { control, setValue } = useFormContext();
  const { iconConfig } = useVNFieldHelpContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={{
        ...(rules || {}),
        ...(required && { required: `${label} is required` }),
      }}
      render={({ field: { onChange, value, ...fieldRest }, fieldState: { error } }) => (
        <TextField
          select
          hiddenLabel={ver2}
          label={ver2 ? undefined : `${label}${required ? ' *' : ''}`}
          size="small"
          error={!!error}
          helperText={error?.message || (!showFieldHelp ? tooltip : undefined)}
          disabled={disabled}
          {...fieldRest}
          value={value ?? ''}
          onChange={async (e) => {
            const newValue = String(e.target.value);
            const oldValue = String(value ?? '');
            if (onBeforeChange) {
              const allowed = await onBeforeChange(newValue, oldValue);
              if (!allowed) {
                onChange(oldValue);
                return;
              }
            }
            onChange(newValue);
          }}
          SelectProps={{
            IconComponent: (props) => <ExpandMore {...props} fontSize="small" />,
            renderValue: renderValue ? (selected) => renderValue(selected as string | number) : undefined,
            MenuProps: {
              disableScrollLock: true,
              sx: { maxHeight: '50%', maxWidth: '50%' },
            },
          }}
          InputProps={{
            endAdornment:
              iconConfig || (clearOption && value) ? (
                <>
                  {clearOption && value ? (
                    <InputAdornment position="end" className="vn-field-help__clear-adornment">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setValue(name, '');
                        }}
                        edge="end"
                        aria-label="Clear"
                      >
                        <ClearOutlined fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null}
                  {iconConfig && (
                    <InputAdornment position="end" className="vn-field-help__input-adornment">
                      <VNFieldHelpIconButton {...iconConfig} />
                    </InputAdornment>
                  )}
                </>
              ) : null,
          }}
        >
          {options.map((option) => {
            const key = String(option.value);
            if (hasItemHelp) {
              return (
                <VNFieldHelpMenuItem
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  itemTooltip={dropDownItemHelperTooltip?.[key]}
                  itemHelperText={dropDownItemHelperText?.[key]}
                />
              );
            }
            return (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            );
          })}
        </TextField>
      )}
    />
  );
};

const VNSelect = ({
  name,
  label,
  options,
  renderValue,
  disabled = false,
  tooltip = '',
  defaultValue = '',
  required = false,
  rules = {},
  clearOption = false,
  onBeforeChange,
  ver2 = false,
  ...fieldHelpProps
}: Props) => {
  const { watch } = useFormContext();
  const currentValue = watch(name);
  const normalized = normalizeFieldHelpProps(fieldHelpProps);
  const { dropDownItemHelperTooltip, dropDownItemHelperText } = normalized;
  const showFieldHelp = hasFieldHelpIcon(fieldHelpProps);
  const hasItemHelp = Boolean(
    (dropDownItemHelperTooltip && Object.keys(dropDownItemHelperTooltip).length > 0) ||
      (dropDownItemHelperText && Object.keys(dropDownItemHelperText).length > 0)
  );

  const fieldProps: SelectFieldProps = {
    name,
    label,
    disabled,
    required,
    tooltip,
    showFieldHelp,
    clearOption,
    options,
    renderValue,
    hasItemHelp,
    dropDownItemHelperTooltip,
    dropDownItemHelperText,
    onBeforeChange,
    defaultValue,
    rules,
    ver2,
  };

  const field = <VNSelectField {...fieldProps} />;

  return (
    <div className={`vn-select${ver2 ? ' vn-field--label-above' : ''}`}>
      {ver2 && label ? <VNFieldTopLabel label={label} required={required} /> : null}
      {showFieldHelp ? (
        <VNFieldHelpLayout
          label={label}
          required={required}
          iconAttach="adornment"
          className={['vn-field-help--select', clearOption && currentValue ? 'vn-field-help--select-clear' : '']
            .filter(Boolean)
            .join(' ')}
          {...fieldHelpProps}
        >
          {field}
        </VNFieldHelpLayout>
      ) : (
        field
      )}
    </div>
  );
};

export default VNSelect;
